import React from "react";

import { EyeOff } from "lucide-react";

import { useAppSelector } from "../../Store/Hooks";
import type { SignalPresentation } from "../../Store/Reducers/SignalFeedSlice";

/**
 * Signal Administration's org-level presentation policy, as returned with
 * the signal feed (signals-design-note.md §11 surface matrix):
 *
 * - `enabled`  — normal: Open Actions, cards, tiers, counts, CTAs.
 * - `disabled` — Open Actions tab stays visible but is not clickable; no
 *                signal output anywhere.
 * - `hidden`   — Open Actions entry points (Actions tab, CTAs) are removed;
 *                no signal output.
 *
 * Inactive service makes the *effective* surface Disabled (or keeps Hidden)
 * while the *configured* Open Actions choice is preserved. Inactive +
 * Enabled still opens Actions to the off-notice + read-only history; only
 * a configured Disabled choice greys out / blocks the Actions tab.
 *
 * Until the first feed response arrives the policy is unknown:
 * `actionsSurface` reads as hidden (matches platform default_config) so
 * signal output and CTAs stay off, but `status` says the policy isn't
 * known yet. Anything that would *leave* the Actions tab because it is
 * Hidden or configured Disabled must wait for `status === "ready"` —
 * otherwise a direct ?tab=actions load bounces away before the policy
 * arrives (S-02), and a failed first feed would hide the tab for good.
 */
export interface SignalPresentationState {
  presentation: SignalPresentation | null;
  /** loading: no policy yet; error: the feed failed before one arrived. */
  status: "loading" | "error" | "ready";
  signalsVisible: boolean;
  /** What users effectively get (Inactive may force Disabled). */
  actionsSurface: SignalPresentation["effective_actions_surface"];
  /** Admin's saved Open Actions choice (Enabled / Disabled / Hidden). */
  configuredActionsSurface: SignalPresentation["actions_surface"];
}

export const useSignalPresentation = (): SignalPresentationState => {
  const presentation = useAppSelector(state => state.signalFeed.presentation);
  const feedError = useAppSelector(state => state.signalFeed.error);
  const status = presentation ? "ready" : feedError ? "error" : "loading";
  return {
    presentation,
    status,
    signalsVisible: presentation ? presentation.signals_visible : false,
    actionsSurface: presentation?.effective_actions_surface ?? "hidden",
    configuredActionsSurface: presentation?.actions_surface ?? "hidden",
  };
};

export const SIGNALS_OFF_MESSAGE =
  "Signal actions are turned off for your organization.";

/** Policy explanation shown where Open Actions would be. */
export const SignalsOffNotice: React.FC<{ showHistoryHint?: boolean }> = ({
  showHistoryHint = false,
}) => (
  <div
    role="status"
    className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
  >
    <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
    <div>
      <div className="text-[13px] font-semibold text-gray-800">
        {SIGNALS_OFF_MESSAGE}
      </div>
      <div className="mt-0.5 text-xs text-gray-500">
        Your administrator controls this setting.
        {showHistoryHint &&
          " Past signal activity is still available below as read-only history."}
      </div>
    </div>
  </div>
);
